import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SouthIndianChart from './components/SouthIndianChart';
import SpiritualCTA from './components/CTA/SpiritualCTA';
import PersonalisedReportPopup from './components/PersonalisedReportPopup';
import ksLogo from './logo-ks-new.png';
import { useAuth } from './hooks/useAuth';
import { usePayment, isWithinReportDeliveryWindow } from './hooks/usePayment';
import PaymentModal from './components/PaymentModal';
import PersonalAnswerTab from './components/PersonalAnswerTab';
import AskKundliQuestionTab from './components/AskKundliQuestionTab';
import ShastraProfileQuestionModal from './components/ShastraProfileQuestionModal';
import MobileAppCTA from './components/MobileAppCTA';
import {
  COPY_149_EXCLUDES_ANSWER,
  MICRO_FULL_REPORT,
  PRICE_FULL_REPORT,
  COPY_UPGRADE_HEADLINE,
  COPY_CHECKOUT_NOTE,
  COPY_POST_REPORT_PAYMENT,
  PRICE_UPGRADE_DELTA,
  PRICE_KUNDLI_QUESTION,
  PRICE_KUNDLI_QUESTION_OLD,
} from './constants/pricingCopy';
import StruckPrice from './components/StruckPrice';
import { useFreeQuestionEligibility } from './hooks/useFreeQuestionEligibility';
import './Dashboard.css';
import { useLanguage } from './context/LanguageContext';

const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

const PLANET_COLORS = {
  sun: '#FFA500',
  moon: '#C0C0C0',
  mercury: '#8C7853',
  venus: '#FFC649',
  mars: '#FF6B47',
  jupiter: '#C88B3A',
  saturn: '#FAD5A5',
  rahu: '#8B7355',
  ketu: '#6B4423'
};

const PLANET_NAMES = {
  sun: 'Sun',
  moon: 'Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  rahu: 'Rahu',
  ketu: 'Ketu'
};

const PLANET_SHORT = {
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke'
};

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];


// Helper function to get ordinal house name (1st House, 2nd House, etc.)
const getHouseName = (houseNumber) => {
  return `${houseNumber}${houseNumber === 1 ? 'st' : houseNumber === 2 ? 'nd' : houseNumber === 3 ? 'rd' : 'th'} House`;
};

// Helper function to get Nakshatra name
const getNakshatraName = (nakshatra) => {
  if (!nakshatra) return '-';
  if (typeof nakshatra === 'object') return nakshatra.name || '-';
  return nakshatra;
};



function Dashboard({ data, onBack, birthData, onProfileSaved, profileId, userId, onNavigateToAddMember }) {
  const { t } = useTranslation();
  const { isHindi } = useLanguage();
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  // ── Payment modal state ──
  const [payModal, setPayModal]         = useState({ open: false, initialPlan: 'report' });

  // ── Per-member purchase status ──
  const effectiveUserId    = userId || user?.id;
  const effectiveMemberId  = profileId;
  const {
    accessLevel,
    isDashboard: isPremiumUser,
    isReport: isReportUser,
    reportPurchaseAt,
    pdfAddonPurchased,
    refresh: refreshAccess,
    loading: accessLoading,
  } = usePayment(effectiveUserId, effectiveMemberId);

  /** Shastra AI “first question free” — UI hint only; backend re-verifies at submit time. */
  const { freeQuestionAvailable: shastraFreeEligible } = useFreeQuestionEligibility(effectiveUserId);

  const showReport24hDeliveryCopy =
    isReportUser && isWithinReportDeliveryWindow(reportPurchaseAt);

  const openPayment = (plan = 'report') => setPayModal({ open: true, initialPlan: plan });
  const handlePaySuccess = (accessLevel) => {
    setPayModal({ open: false, initialPlan: 'report' });
    refreshAccess();
  };

  const [planDetailsOpen, setPlanDetailsOpen] = useState(false);
  const [shastraProfileModalOpen, setShastraProfileModalOpen] = useState(false);
  const skipShastraWelcomeOnceRef = useRef(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('ks_open_ask_tab') === '1') {
        sessionStorage.removeItem('ks_open_ask_tab');
        skipShastraWelcomeOnceRef.current = true;
        setActiveTab('ask-question');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  /** After load: if profile has a 10+ char question and no paid answer for it yet, offer Shastra unlock (dismiss “Baad mein” = hide until next browser session). */
  useEffect(() => {
    setShastraProfileModalOpen(false);
    const q = (birthData?.question != null ? String(birthData.question) : '').trim();
    if (!effectiveUserId || !effectiveMemberId || q.length < 10) {
      return;
    }
    if (skipShastraWelcomeOnceRef.current) {
      skipShastraWelcomeOnceRef.current = false;
      return;
    }
    const dismissKey = `ks_shastra_welcome_dismissed_${effectiveMemberId}`;
    try {
      if (sessionStorage.getItem(dismissKey) === '1') return;
    } catch (_) {
      /* ignore */
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/kundli-questions?userId=${encodeURIComponent(effectiveUserId)}&profileId=${encodeURIComponent(effectiveMemberId)}`
        );
        const json = await res.json();
        if (cancelled) return;
        if (!json.success) return;
        const items = json.items || [];
        const hasAnswered = items.some(
          (row) =>
            row?.answer &&
            String(row.answer).trim() &&
            (row.question || '').trim() === q
        );
        if (hasAnswered) return;
        if (cancelled) return;
        setShastraProfileModalOpen(true);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveUserId, effectiveMemberId, birthData?.question]);

  useEffect(() => {
    if (activeTab === 'insights') setActiveTab('overview');
  }, [activeTab]);
  
  if (!data || !data.planets) {
    return null;
  }



  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const hasPersonalQuestion = !!(birthData?.question && String(birthData.question).trim());
  const profileQuestionTrim = (birthData?.question != null ? String(birthData.question) : '').trim();
  /** Align with AskKundliQuestionTab: "Unlock" only when there is a real 10+ char question for this profile */
  const shastraCanUnlock = profileQuestionTrim.length >= 10;

  /** Plain-text price for aria-labels / title attrs (no JSX allowed there). */
  const shastraPriceText = shastraFreeEligible ? 'FREE' : `${PRICE_KUNDLI_QUESTION} (was ${PRICE_KUNDLI_QUESTION_OLD})`;
  /** Visible “price” chip/label — free vs struck-through ₹51 → ₹21. */
  const shastraPriceLabel = shastraFreeEligible
    ? (<>FREE <span aria-hidden>🎁</span></>)
    : (<StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />);

  /** Next to page title: instant Q&A CTA when no sidebar widget (no Q yet, or Full Report row below tabs) */
  const showTitleInlineAskCta =
    !!effectiveUserId &&
    !!effectiveMemberId &&
    activeTab !== 'ask-question' &&
    (!hasPersonalQuestion || isReportUser);

  const tabs = [
    { id: 'overview',    label: 'Overview',       hint: 'Lagna, Moon, nakshatra & quick facts', icon: '📊', locked: false },
    { id: 'predictions', label: 'Predictions',    hint: 'Personalised predictions — on our app', icon: '🔮', locked: false },
    { id: 'charts',      label: 'Kundli Chart',   hint: 'Chart map, houses & planet key',        icon: '🌐', locked: false },
    { id: 'ask-question', label: 'Your Answer',  hint: shastraFreeEligible ? 'Shastra AI one-question — your first answer is FREE' : `Shastra AI one-question — ${PRICE_KUNDLI_QUESTION} (was ${PRICE_KUNDLI_QUESTION_OLD})`, icon: '✨', locked: false },
    { id: 'planets',     label: 'Planets',        hint: 'Graha table: house, sign, degree',     icon: '🪐', locked: false },
    { id: 'mahadasha',   label: 'Mahadasha',      hint: 'Mahadasha & antardasha periods',       icon: '⏳', locked: false },
    { id: 'karmic',      label: 'Karmic Insights',hint: 'Karmic themes from chart & nakshatra', icon: '🌀', locked: false },
    { id: 'numerology',  label: 'Numerology',     hint: 'Core numbers from name & birth date',  icon: '🔢', locked: false },
  ];

  const renderSectionNavButton = (tab) => {
    const isAnswerTab = tab.id === 'ask-question';
    const active = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => {
          setActiveTab(tab.id);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        title={tab.locked ? `${tab.label}: ${tab.hint} (unlock to read full)` : `${tab.label}: ${tab.hint}`}
        style={{ position: 'relative', minWidth: 'min-content' }}
        className={`flex flex-col items-start gap-0.5 px-3 sm:px-4 py-3 md:py-2.5 font-semibold transition-all text-left min-h-[3.25rem] md:min-h-0 rounded-xl md:rounded-none flex-shrink-0 w-full md:w-auto ${
          isAnswerTab && !tab.locked
            ? active
              ? 'text-amber-950 border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md ring-2 ring-amber-400/40 md:ring-0 md:border-b-2 md:border-amber-500 md:border-t-0 md:border-x-0 md:shadow-none md:bg-saffron-50'
              : 'text-amber-900 border-2 border-amber-400/90 bg-gradient-to-br from-amber-50/95 to-orange-50/80 shadow-sm hover:brightness-[1.02] md:border-0 md:border-b-2 md:border-transparent md:bg-transparent md:shadow-none md:text-saffron-700 md:hover:bg-saffron-50/50'
            : tab.locked
              ? active
                ? 'text-amber-800 border border-amber-200 bg-amber-50/60 cursor-pointer md:border-0 md:border-b-2 md:border-amber-400'
                : 'text-gray-500 border border-stone-100 bg-white/70 hover:text-amber-800 hover:bg-amber-50/50 cursor-pointer md:border-0 md:bg-transparent'
              : active
                ? 'text-saffron-800 border border-saffron-200 bg-saffron-50 md:border-0 md:border-b-2 md:border-saffron-500 md:bg-saffron-50'
                : 'text-saffron-700 border border-saffron-100/80 bg-white/80 hover:text-saffron-800 hover:bg-saffron-50/50 md:border-0 md:bg-transparent'
        }`}
      >
        <span className="flex items-center gap-1 flex-wrap">
          <span className="text-base leading-none">{tab.icon}</span>
          <span className="text-xs sm:text-sm whitespace-nowrap">{tab.label}</span>
          {tab.locked && (
            <span style={{ fontSize:'0.65rem', background:'#fef3c7', color:'#92400e', borderRadius:'999px', padding:'0.05rem 0.28rem', fontWeight:800, border:'1px solid #fcd34d', lineHeight:1.2 }} title="Premium — unlock">🔒</span>
          )}
          {isAnswerTab && !tab.locked && (
            <span
              className="text-[0.6rem] font-extrabold uppercase text-amber-800 bg-amber-100/90 px-1 py-0 rounded sm:text-[0.65rem]"
              title={shastraPriceText}
            >
              {shastraFreeEligible ? 'FREE 🎁' : shastraPriceLabel}
            </span>
          )}
        </span>
        <span className={`text-[10px] sm:text-[11px] font-normal leading-tight w-full ${tab.locked ? 'text-amber-700/85' : 'text-saffron-600/90'}`}>
          {tab.hint}
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-screen app-shell-bg">

      {/* ── Top header bar (mobile: second row = section dropdown below profile actions) ── */}
      <div
        className="sticky top-0 z-[60]"
        style={{
          background: 'linear-gradient(135deg,#78350f,#92400e)',
          boxShadow: '0 2px 8px rgba(0,0,0,.25)',
        }}
      >
        <div
          style={{
            padding: '0.55rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
        <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
          <img src={ksLogo} alt="Kundli Shastra" style={{ height:'28px', width:'auto', objectFit:'contain', display:'block' }} />
          <span style={{ color:'#fff', fontWeight:700, fontSize:'0.9rem' }}>Kundli Shastra</span>
          {user && <span style={{ color:'rgba(255,255,255,.7)', fontSize:'0.78rem' }}>· {user.name || user.email}</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', justifyContent:'flex-end' }}>
          {!accessLoading && accessLevel === 'report' && (
            <button
              type="button"
              onClick={() => setPlanDetailsOpen(true)}
              style={{ background:'rgba(255,255,255,.22)', border:'1px solid rgba(255,255,255,.45)', color:'#fff', padding:'0.28rem 0.65rem', borderRadius:'999px', fontSize:'0.72rem', fontWeight:800, cursor:'pointer', whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:'0.35rem' }}
              title="View plan details"
            >
              <span aria-hidden>✓</span> {PRICE_FULL_REPORT} · Complete Report
            </button>
          )}
          {/* Desktop: Shastra CTA in header */}
          <button
            type="button"
            aria-pressed={activeTab === 'ask-question'}
            onClick={() => setActiveTab('ask-question')}
            className="hidden md:inline-flex"
            style={{
              background: activeTab === 'ask-question' ? 'rgba(255,251,235,.38)' : 'rgba(254,243,199,.22)',
              border: activeTab === 'ask-question' ? '1px solid rgba(255,251,235,.9)' : '1px solid rgba(253,230,138,.45)',
              color: '#fffbeb',
              padding: '0.2rem 0.7rem 0.22rem',
              borderRadius: '999px',
              cursor: 'pointer',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '0.06rem',
              boxShadow: '0 2px 10px rgba(0,0,0,.12)',
              lineHeight: 1.2,
            }}
            title={
              shastraCanUnlock
                ? (shastraFreeEligible ? 'Get your first answer FREE — personalised' : `Unlock answer — ${shastraPriceText} · personalised`)
                : (isHindi
                  ? `Shastra AI — pehle apna sawaal likhein (min. 10 characters), phir ${shastraFreeEligible ? 'FREE' : shastraPriceText}`
                  : `Shastra AI — ask your question (min. 10 characters), then ${shastraFreeEligible ? 'FREE' : shastraPriceText}`)
            }
          >
            <span style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
              {shastraCanUnlock ? (
                shastraFreeEligible ? (
                  <><span aria-hidden>🎁</span> Get Your First Answer FREE</>
                ) : (
                  <><span aria-hidden>👉</span> Unlock Your Answer → {shastraPriceLabel}</>
                )
              ) : isHindi ? (
                <><span aria-hidden>✨</span> Shastra AI — apna sawaal likhein → {shastraPriceLabel}</>
              ) : (
                <><span aria-hidden>✨</span> Shastra AI — ask your question → {shastraPriceLabel}</>
              )}
            </span>
            <span style={{ fontSize: '0.58rem', fontWeight: 500, opacity: 0.88, whiteSpace: 'nowrap' }}>
              {shastraCanUnlock
                ? (shastraFreeEligible ? 'No payment required' : 'Personalised guidance')
                : (isHindi ? 'Pehle apna sawaal (10+ characters)' : 'Ask your question first (10+ characters)')}
            </span>
          </button>
          {/* Mobile: premium PDF (Shastra AI unlock is a separate chip in tab bar) */}
          {!accessLoading && !isReportUser && (
            <button
              type="button"
              onClick={() => openPayment('report')}
              className="md:hidden inline-flex"
              style={{
                background: '#fef3c7',
                color: '#78350f',
                border: '2px solid #fde68a',
                padding: '0.28rem 0.65rem',
                borderRadius: '999px',
                fontSize: '0.68rem',
                fontWeight: 800,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,.18)',
                lineHeight: 1.25,
              }}
              title={`Full kundli analysis premium PDF — ${PRICE_FULL_REPORT}`}
            >
              Order PDF — {PRICE_FULL_REPORT}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (onBack) {
                onBack(); // Go back to previous page/profile selection list
              } else {
                onNavigateToAddMember?.();
              }
            }}
            style={{ background:'rgba(255,255,255,.15)', border:'1.5px solid rgba(255,255,255,.4)', color:'#fff', padding:'0.35rem 0.9rem', borderRadius:'999px', fontSize:'0.78rem', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
          >
            👤 Select Profile
          </button>
          {onBack && (
            <button onClick={onBack}
              style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.3)', color:'#fff', padding:'0.35rem 0.75rem', borderRadius:'999px', fontSize:'0.78rem', cursor:'pointer' }}>
              ← Back
            </button>
          )}
          <button
            onClick={async () => { await signOut(); window.location.reload(); }}
            style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.25)', color:'rgba(255,255,255,.8)', padding:'0.35rem 0.75rem', borderRadius:'999px', fontSize:'0.75rem', cursor:'pointer' }}
          >
            Sign Out
          </button>
        </div>
        </div>

        {/* Mobile only: kundli section picker — min 16px text helps native picker readability */}
        <div className="md:hidden border-t border-white/20 px-3 py-2.5">
          <label htmlFor="ks-mobile-dash-section" className="sr-only">
            Kundli section
          </label>
          <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-wider text-white/85">
            Kundli section
          </p>
          <select
            id="ks-mobile-dash-section"
            value={activeTab}
            onChange={(e) => {
              setActiveTab(e.target.value);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="ks-mobile-section-select w-full min-h-[3.25rem] rounded-xl border-2 border-amber-200/95 bg-[#fffbeb] px-4 py-3 text-[17px] leading-snug font-semibold text-amber-950 shadow-md focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300/60"
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id} style={{ fontSize: '17px', padding: '0.5rem 0' }}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mobile: sticky Shastra shortcut under header so unlock is always one tap away */}
      {effectiveUserId && effectiveMemberId && activeTab !== 'ask-question' && (
        <div
          className="lg:hidden sticky top-14 max-md:top-[6.75rem] z-[55] border-b border-amber-200/70 bg-gradient-to-r from-[#fffbeb] via-amber-50/95 to-orange-50/90 px-3 py-2 shadow-[0_4px_12px_rgba(120,53,15,.08)]"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
        >
          <button
            type="button"
            onClick={() => {
              setActiveTab('ask-question');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="w-full min-h-[52px] rounded-xl px-3 py-3 font-extrabold text-[1rem] text-[#fffbeb] shadow-md active:scale-[0.99] transition-transform"
            style={{
              background: 'linear-gradient(90deg,#78350f,#b45309)',
              boxShadow: '0 4px 14px rgba(120,53,15,.28)',
            }}
            aria-current={activeTab === 'ask-question' ? 'page' : undefined}
          >
            {shastraCanUnlock ? (
              shastraFreeEligible ? (
                <>🎁 Get Your First Answer FREE</>
              ) : (
                <>👉 Unlock your answer — {shastraPriceLabel}</>
              )
            ) : (
              <>✨ Your answer — {shastraPriceLabel} · tap to type question</>
            )}
          </button>
        </div>
      )}

      {/* ── Legacy: had old “dashboard” purchase only → promote Full Report ── */}
      {accessLevel === 'dashboard' && !isReportUser && !accessLoading && (
        <div
          style={{
            background: 'linear-gradient(120deg, #fffbeb 0%, #fef3c7 45%, #fde68a 100%)',
            borderBottom: '2px solid rgba(180, 83, 9, 0.35)',
            padding: '0.75rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            boxShadow: '0 6px 24px rgba(120,53,15,.12)',
          }}
        >
          <div style={{ textAlign: 'center', minWidth: 'min(100%, 280px)' }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: '0.95rem', color: '#5c2e0e', letterSpacing: '0.02em' }}>
              🔮 Your personal answer is ready
            </p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#78350f', lineHeight: 1.45 }}>
              Unlock your exact answer with full kundli report
            </p>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.72rem', color: '#92400e' }}>{COPY_UPGRADE_HEADLINE} · {COPY_CHECKOUT_NOTE}</p>
          </div>
          <button
            type="button"
            onClick={() => openPayment('report')}
            style={{
              background: 'linear-gradient(90deg, #78350f, #b45309)',
              color: '#fffbeb',
              border: 'none',
              borderRadius: '999px',
              padding: '0.5rem 1.15rem',
              fontWeight: 800,
              fontSize: '0.82rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(120,53,15,.35)',
            }}
          >
            Unlock premium PDF – {PRICE_UPGRADE_DELTA}
          </button>
          <ul style={{ margin: 0, padding: '0 0 0 1rem', fontSize: '0.72rem', color: '#57534e', lineHeight: 1.45, listStyle: 'none' }}>
            {MICRO_FULL_REPORT.map((line) => (
              <li key={line}>
                <span style={{ color: '#059669', fontWeight: 700 }}>✔</span> {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Payment Modal ── */}
      <PaymentModal
        isOpen={payModal.open}
        initialPlan={payModal.initialPlan}
        onClose={() => setPayModal({ open: false, initialPlan: 'report' })}
        onSuccess={handlePaySuccess}
        userId={effectiveUserId}
        familyMemberId={effectiveMemberId}
        memberName={birthData?.name || user?.name}
        userEmail={user?.email}
        userName={user?.name || user?.email}
        hasQuestion={!!(birthData?.question)}
        isDashboardUpgrade={isPremiumUser && !isReportUser}
      />

      {/* Plan details (opened from header chip) */}
      {planDetailsOpen && !accessLoading && (accessLevel === 'report' || accessLevel === 'dashboard') && (
        <>
          <div
            onClick={() => setPlanDetailsOpen(false)}
            className="ks-modal-scrim"
            style={{ position:'fixed', inset:0, zIndex:10040 }}
            role="presentation"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="plan-details-title"
            style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:10041, width:'min(420px,92vw)', background:'#ffffff', borderRadius:'16px', boxShadow:'0 8px 30px rgba(0,0,0,0.06), 0 24px 48px rgba(0,0,0,0.1)', border:'1px solid rgb(254 215 170 / 0.55)', overflow:'hidden' }}
          >
            <div style={{ padding:'1rem 1.25rem', borderBottom:'1px solid #fde68a', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem' }}>
              <span id="plan-details-title" style={{ fontWeight:800, fontSize:'1rem', color:'#78350f' }}>Your plan</span>
              <button type="button" onClick={() => setPlanDetailsOpen(false)} style={{ background:'#fef3c7', border:'1px solid #fcd34d', width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:18, lineHeight:1, color:'#78350f' }} aria-label="Close">×</button>
            </div>
            <div style={{ padding:'1.25rem' }}>
              {accessLevel === 'report' ? (
                <>
                  <p style={{ margin:'0 0 0.75rem', display:'flex', flexWrap:'wrap', alignItems:'center', gap:'0.5rem', fontWeight:700, color:'#065f46', fontSize:'0.95rem' }}>
                    <span style={{ background:'#059669', color:'#fff', padding:'0.2rem 0.5rem', borderRadius:6, fontSize:'0.65rem', fontWeight:800, letterSpacing:'0.04em' }}>PAYMENT SUCCESSFUL</span>
                    Complete Report ({PRICE_FULL_REPORT})
                  </p>
                  <p style={{ margin:0, fontSize:'0.88rem', lineHeight:1.55, color:'#064e3b' }}>
                    {showReport24hDeliveryCopy ? (
                      <>{COPY_POST_REPORT_PAYMENT}. Your personalised PDF will include your answer.</>
                    ) : (
                      <>Your <strong>premium PDF ({PRICE_FULL_REPORT})</strong> is active. {COPY_POST_REPORT_PAYMENT}</>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <p style={{ margin:'0 0 0.75rem', display:'flex', flexWrap:'wrap', alignItems:'center', gap:'0.5rem', fontWeight:700, color:'#78350f', fontSize:'0.95rem' }}>
                    <span style={{ background:'#d97706', color:'#fff', padding:'0.2rem 0.5rem', borderRadius:6, fontSize:'0.65rem', fontWeight:800, letterSpacing:'0.04em' }}>PAYMENT SUCCESSFUL</span>
                    Earlier plan (insights access)
                  </p>
                  <p style={{ margin:0, fontSize:'0.88rem', lineHeight:1.55, color:'#78350f' }}>
                    All <strong>8 dashboard sections</strong> are available in the app for everyone now. Your earlier payment is still on file.
                  </p>
                  <p style={{ margin:'0.65rem 0 0', fontSize:'0.78rem', color:'#92400e', fontWeight:600, lineHeight:1.45, background:'#fff7ed', padding:'0.5rem 0.65rem', borderRadius:'8px', border:'1px solid #fde68a' }}>
                    {COPY_149_EXCLUDES_ANSWER}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setPlanDetailsOpen(false); openPayment('report'); }}
                    style={{ marginTop:'0.85rem', width:'100%', padding:'0.65rem', background:'linear-gradient(90deg,#78350f,#b45309)', color:'#fffbeb', border:'none', borderRadius:'0.6rem', fontWeight:800, fontSize:'0.88rem', cursor:'pointer', boxShadow:'0 4px 14px rgba(120,53,15,.3)' }}
                  >
                    Upgrade to premium PDF – {PRICE_UPGRADE_DELTA}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setPlanDetailsOpen(false)}
                style={{ marginTop:'0.75rem', width:'100%', padding:'0.55rem', background:'#fef3c7', color:'#78350f', border:'1px solid #fcd34d', borderRadius:'0.6rem', fontWeight:600, fontSize:'0.85rem', cursor:'pointer' }}
              >
                Got it
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Premium PDF vs Shastra AI — desktop strip only (mobile: header PDF + no duplicate banner) ── */}
      {hasPersonalQuestion && !isReportUser && !accessLoading && (
        <div
          className="hidden md:flex"
          style={{ background:'linear-gradient(90deg,#78350f,#b45309)', color:'#fff', padding:'0.65rem 1rem', textAlign:'center', fontSize:'0.82rem', fontWeight:600, alignItems:'center', justifyContent:'center', gap:'0.7rem', flexWrap:'wrap', boxShadow:'0 2px 8px rgba(0,0,0,.2)' }}
        >
          <span>
            <strong>Full kundli analysis premium PDF report</strong> ({PRICE_FULL_REPORT}) — detailed chart analysis, emailed to you.
            {' '}
            <span style={{ opacity:0.95, fontWeight:500 }}>
              {shastraFreeEligible ? (
                <>🎁 Pehla personal sawaal FREE. Uske baad har sawaal sirf <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />.</>
              ) : (
                <>Har sawaal ka turant jawab <strong>Shastra AI</strong> se — sirf <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />.</>
              )}
            </span>
          </span>
          <button
            type="button"
            onClick={() => openPayment('report')}
            style={{ background:'#fef3c7', color:'#78350f', padding:'0.34rem 0.95rem', borderRadius:'999px', fontSize:'0.75rem', fontWeight:900, border:'2px solid #fde68a', cursor:'pointer', whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(0,0,0,.2)' }}
          >
            Order premium PDF — {PRICE_FULL_REPORT}
          </button>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 flex flex-col">
        {/* Title first; on mobile, main tab content comes before the section dropdown */}
        <div className="order-1 flex flex-col gap-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-0 md:mb-6"
        >
          <div
            id={hasPersonalQuestion && !isReportUser ? 'personal-answer-upsell' : undefined}
            className="flex flex-col gap-4 scroll-mt-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6"
          >
            <div className="min-w-0 flex-1 lg:pr-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <h1 className="text-3xl font-bold text-saffron-700 mb-1">{t('dashboard.title')}</h1>
                  <p className="text-saffron-600 text-sm">{birthData?.name ? `Viewing kundli for ${birthData.name}` : t('dashboard.subtitle')}</p>
                  {hasPersonalQuestion && !isReportUser && (
                    <p className="mt-1.5 max-md:hidden lg:hidden text-xs font-medium text-amber-900/80 sm:text-sm">
                      {shastraFreeEligible ? (
                        <>🎁 Instant answer: your first Shastra AI question is FREE.</>
                      ) : accessLevel === 'dashboard' ? (
                        <>Instant answer: Shastra AI ({shastraPriceLabel}). Premium PDF alag — banner dekhein.</>
                      ) : (
                        <>Kundli ready — Shastra AI ({shastraPriceLabel}) se turant answer.</>
                      )}
                    </p>
                  )}
                </div>
                {showTitleInlineAskCta && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab('ask-question');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="group ks-inline-shastra-ai flex w-full shrink-0 flex-col items-start gap-0.5 rounded-full border border-amber-300/70 bg-gradient-to-br from-amber-50/95 via-white to-orange-50/85 px-3 py-1.5 text-left shadow-[0_2px_10px_rgba(120,53,15,.1)] transition hover:border-amber-400/80 hover:shadow-[0_4px_14px_rgba(120,53,15,.14)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 focus-visible:ring-offset-2 sm:mt-0.5 sm:w-auto"
                    title={shastraCanUnlock ? undefined : (isHindi ? 'Pehle apna sawaal likhein (kam se kam 10 characters)' : 'Ask your question first (at least 10 characters)')}
                  >
                    <span className="text-[0.72rem] font-semibold leading-tight text-amber-950 sm:text-[0.74rem]">
                      {shastraCanUnlock ? (
                        shastraFreeEligible ? (
                          <><span aria-hidden>🎁</span> Get Your First Answer FREE</>
                        ) : (
                          <><span aria-hidden>👉</span> Unlock Your Answer → {shastraPriceLabel}</>
                        )
                      ) : isHindi ? (
                        <><span aria-hidden>✨</span> Shastra AI — apna sawaal likhein → {shastraPriceLabel}</>
                      ) : (
                        <><span aria-hidden>✨</span> Shastra AI — ask your question → {shastraPriceLabel}</>
                      )}
                    </span>
                    <span className="text-[0.58rem] font-medium leading-tight text-amber-800/75">
                      {shastraCanUnlock
                        ? (shastraFreeEligible ? 'No payment required' : 'Personalised guidance')
                        : (isHindi ? 'Pehle apna sawaal (10+ characters)' : 'Ask your question first (10+ characters)')}
                    </span>
                  </button>
                )}
              </div>
            </div>
            {hasPersonalQuestion && !isReportUser && (
              <div className="hidden w-full shrink-0 lg:block lg:w-[min(100%,22rem)] lg:pt-0.5">
                <PersonalAnswerTab
                  placement="header"
                  question={birthData.question}
                  onGetReport={() => openPayment('report')}
                  onGetPdfAddon={() => openPayment('pdf_addon')}
                  hasPaidReport={isReportUser}
                  hasDashboardUnlock={accessLevel === 'dashboard'}
                  hasPdfAddon={pdfAddonPurchased}
                  show24hDeliveryMessage={showReport24hDeliveryCopy}
                  onNavigateToAskQuestion={() => setActiveTab('ask-question')}
                  freeQuestionAvailable={shastraFreeEligible}
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Complete Report purchased: question + confirmation (below tabs nav) */}
        {hasPersonalQuestion && isReportUser && (
          <div id="personal-answer-upsell" className="mb-6 scroll-mt-4">
            <PersonalAnswerTab
              question={birthData.question}
              onGetReport={() => openPayment('report')}
              hasPaidReport={isReportUser}
              show24hDeliveryMessage={showReport24hDeliveryCopy}
              onNavigateToAskQuestion={() => setActiveTab('ask-question')}
              freeQuestionAvailable={shastraFreeEligible}
            />
          </div>
        )}
        </div>

        {/* Tabs nav — desktop/tablet only (mobile picker lives under sticky header).
            Single row, horizontally scrollable when it doesn't fit — never wraps. */}
        <div className="order-3 md:order-2 ks-dash-panel mb-6 hidden md:block md:p-0 border-saffron-200 md:border-0">
          <div className="ks-dash-tabs-scroll gap-1 border-b border-saffron-200 rounded-none">
            {tabs.map(renderSectionNavButton)}
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          className="order-2 md:order-3"
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
              {/* Quick Facts */}
              <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-4">Quick Facts</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Current Dasha */}
                  {data.mahadasha && data.mahadasha.current && (
                    <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-saffron-700">Current Dasha:</span>
                        <span className="text-sm font-bold text-saffron-800">
                          {data.mahadasha.current.lord}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Birth Nakshatra */}
                  {data.nakshatra && (
                    <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-saffron-700">Nakshatra:</span>
                        <span className="text-sm font-bold text-saffron-800">
                          {data.nakshatra.name} Pada {data.nakshatra.pada}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Lagna Sign */}
                  {data.lagna && (
                    <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-saffron-700">Lagna:</span>
                        <span className="text-sm font-bold text-saffron-800">
                          {data.lagna.sign} {data.lagna.deg}°
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Moon Sign */}
                  {data.chandra_rashi && (
                    <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-saffron-700">Moon Sign:</span>
                        <span className="text-sm font-bold text-saffron-800">
                          {data.chandra_rashi.sign} {data.chandra_rashi.deg}°
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>


              {/* Planetary Summary */}
              {data.planets && (
                <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                  <h2 className="text-2xl font-bold text-saffron-700 mb-4">Planetary Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(data.planets).map(([key, planet]) => {
                      const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                      const planetSignIndex = SIGNS.indexOf(planet.sign);
                      const houseNumber = ((planetSignIndex - ascendantIndex + 12) % 12) + 1;
                      
                      return (
                        <div key={key} className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-lg p-3 border border-saffron-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-5 h-5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: PLANET_COLORS[key] || '#666' }}
                            />
                            <span className="font-bold text-saffron-800 text-sm">{PLANET_NAMES[key] || key}</span>
                          </div>
                          <div className="text-xs text-saffron-700 space-y-1">
                            <div><span className="font-semibold">{getHouseName(houseNumber)}</span> • {planet.sign}</div>
                            <div>{planet.deg}° • {getNakshatraName(planet.nakshatra)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* House Analysis */}
              {data.planets && data.lagna && (
                <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                  <h2 className="text-2xl font-bold text-saffron-700 mb-4">House Analysis</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Array.from({ length: 12 }, (_, i) => {
                      const houseNumber = i + 1;
                      const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                      const signIndex = (ascendantIndex + i) % 12;
                      const sign = SIGNS[signIndex];
                      const planetsInHouse = Object.entries(data.planets)
                        .filter(([key, planet]) => planet.sign === sign)
                        .map(([key]) => key);
                      
                      const houseMeanings = {
                        1: 'Self, Personality',
                        2: 'Wealth, Family',
                        3: 'Siblings, Courage',
                        4: 'Mother, Home',
                        5: 'Children, Intellect',
                        6: 'Enemies, Health',
                        7: 'Marriage, Partnerships',
                        8: 'Longevity, Research',
                        9: 'Father, Luck',
                        10: 'Career, Profession',
                        11: 'Gains, Friends',
                        12: 'Losses, Spirituality'
                      };
                      
                      return (
                        <div key={houseNumber} className={`bg-gradient-to-br ${houseNumber === 1 ? 'from-orange-50 to-saffron-50' : 'from-saffron-50 to-cream-50'} rounded-lg p-3 border ${houseNumber === 1 ? 'border-orange-200' : 'border-saffron-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-saffron-800">{getHouseName(houseNumber, t)}</span>
                            <span className="text-xs text-saffron-600">{houseMeanings[houseNumber]}</span>
                          </div>
                          <div className="text-xs text-saffron-700">
                            <div className="mb-1">{sign}</div>
                            {planetsInHouse.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {planetsInHouse.map((key) => (
                                  <span
                                    key={key}
                                    className="px-2 py-0.5 rounded text-white text-xs font-semibold"
                                    style={{ backgroundColor: PLANET_COLORS[key] || '#666' }}
                                  >
                                    {PLANET_SHORT[PLANET_NAMES[key]]}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-saffron-400 italic">Empty</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Mobile app promo */}
              <motion.div variants={itemVariants}>
                <MobileAppCTA />
              </motion.div>
            </motion.div>
          )}

          {/* Predictions Tab — app-exclusive, drives installs */}
          {activeTab === 'predictions' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              <motion.div variants={itemVariants} className="ks-dash-panel p-8 text-center">
                <div className="text-6xl mb-4">🔮</div>
                <h2 className="text-2xl font-bold text-saffron-700 mb-3">Your Personalised Predictions</h2>
                <p className="text-saffron-600 max-w-xl mx-auto mb-2 leading-relaxed">
                  Detailed, personalised predictions for career, love, health, and finance —
                  powered by your unique birth chart — are an app-exclusive feature.
                </p>
                <p className="text-saffron-500 max-w-xl mx-auto mb-8 text-sm leading-relaxed">
                  Download the Kundli Shastra app to unlock your predictions, along with faster
                  performance and personalised AI guidance.
                </p>
                <div className="max-w-2xl mx-auto text-left">
                  <MobileAppCTA />
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Planets Tab */}
          {activeTab === 'planets' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-4">Planetary Positions</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-saffron-200">
                    <th className="text-left py-2 text-saffron-700 font-semibold">Planet</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">House</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">Rashi</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">Sign</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">Degrees</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">Nakshatra</th>
                    <th className="text-left py-2 text-saffron-700 font-semibold">Pada</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 12 }, (_, i) => {
                    const houseNumber = i + 1;
                    // Calculate which sign is in this house
                    const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                    const signIndex = (ascendantIndex + i) % 12;
                    const sign = SIGNS[signIndex];
                    const rashiNumber = signIndex + 1;
                    
                    // Find all planets in this house (sign)
                    const planetsInHouse = Object.entries(data.planets)
                      .filter(([key, planet]) => planet.sign === sign)
                      .map(([key, planet]) => ({ key, planet }));
                    
                    // If no planets, show empty row
                    if (planetsInHouse.length === 0) {
                      return (
                        <motion.tr
                          key={`house-${houseNumber}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.05 }}
                          className="border-b border-saffron-100 hover:bg-saffron-50 transition-colors"
                        >
                          <td className="py-3 text-saffron-400 italic">—</td>
                          <td className="py-3">
                            <span className={`font-bold ${houseNumber === 1 ? 'text-orange-600' : 'text-saffron-700'}`}>
                              {houseNumber}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className="font-bold text-saffron-700">
                              {rashiNumber}
                            </span>
                          </td>
                          <td className="py-3 text-saffron-700">{sign}</td>
                          <td className="py-3 text-saffron-400">—</td>
                          <td className="py-3 text-saffron-400">—</td>
                          <td className="py-3 text-saffron-400">—</td>
                        </motion.tr>
                      );
                    }
                    
                    // If planets exist, show them
                    return planetsInHouse.map(({ key, planet }, pIdx) => (
                      <motion.tr
                        key={key}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (i * 0.05) + (pIdx * 0.02) }}
                        className="border-b border-saffron-100 hover:bg-saffron-50 transition-colors"
                      >
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: PLANET_COLORS[key] || '#666' }}
                            />
                            <span className="font-medium text-saffron-800">{PLANET_NAMES[key] || key}</span>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className={`font-bold ${houseNumber === 1 ? 'text-orange-600' : 'text-saffron-700'}`}>
                            {houseNumber}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="font-bold text-saffron-700">
                            {rashiNumber}
                          </span>
                        </td>
                        <td className="py-3 text-saffron-700">{planet.sign}</td>
                        <td className="py-3 text-saffron-600">{planet.deg}°</td>
                        <td className="py-3 text-saffron-600">{getNakshatraName(planet.nakshatra)}</td>
                        <td className="py-3 text-saffron-600">{planet.pada}</td>
                      </motion.tr>
                    ));
                  }).flat()}
                </tbody>
              </table>
            </div>

          </motion.div>
          </motion.div>
          )}

          {/* Kundli Chart Tab */}
          {activeTab === 'charts' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              {/* Birth Chart (D1) */}
              <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-4">{isHindi ? '🌐 Janm Kundli' : '🌐 Birth Chart (Janm Kundli)'}</h2>
                <SouthIndianChart 
                  ascendant={data.lagna?.sign || SIGNS[0]}
                  chartData={data.planets || []}
                />
                <div style={{ marginTop:'1.25rem', background:'linear-gradient(135deg,#fff7ed,#fef3c7)', border:'1.5px solid #f59e0b', borderRadius:'1rem', padding:'1.1rem 1.25rem', textAlign:'center' }}>
                  <p style={{ fontWeight:700, color:'#92400e', fontSize:'0.95rem', marginBottom:'0.35rem' }}>
                    {isHindi ? '📜 Yeh aapki Janm Kundli hai' : '📜 This is your birth chart (Janm Kundli)'}
                  </p>
                  <p style={{ color:'#78350f', fontSize:'0.85rem', margin:0 }}>
                    {isHindi
                      ? 'Neeche house meanings, planet keys aur divisional charts (jab available hon) dekh sakte hain. Mahadasha aur baaki sections neeche ke tabs mein hain.'
                      : 'See house meanings, planet keys, and divisional charts below. Mahadasha and other sections are in the tabs above.'}
                  </p>
                </div>
                
                <div className="mt-6 bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-5 border border-saffron-200">
                  <h3 className="text-lg font-bold text-saffron-700 mb-4">House Meanings (1st-12th House)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    {Array.from({ length: 12 }, (_, i) => {
                      const houseNumber = i + 1;
                      const houseDescriptions = {
                        1: 'Self, Personality, Physical Body',
                        2: 'Wealth, Family, Speech',
                        3: 'Siblings, Courage, Communication',
                        4: 'Mother, Home, Happiness',
                        5: 'Children, Intellect, Creativity',
                        6: 'Enemies, Debts, Diseases, Service',
                        7: 'Marriage, Partnerships, Spouse',
                        8: 'Longevity, Sudden Events, Research',
                        9: 'Father, Guru, Luck, Spirituality',
                        10: 'Career, Profession, Public Image',
                        11: 'Gains, Friends, Desires',
                        12: 'Losses, Expenditure, Foreign Travel'
                      };
                      return (
                        <div key={houseNumber} className="bg-white/70 rounded-lg p-3 border border-saffron-200/50">
                          <span className="font-bold text-saffron-800">{getHouseName(houseNumber)}:</span>
                          <span className="ml-2 text-saffron-700">{houseDescriptions[houseNumber]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Planet Abbreviations (2-digit) */}
                  <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-5 border border-saffron-200">
                    <h3 className="text-lg font-bold text-saffron-700 mb-4">Planet Abbreviations</h3>
                    <div className="space-y-3">
                      {Object.entries(PLANET_SHORT).map(([key, short]) => {
                        // Ensure 2-digit abbreviation
                        const abbreviation = short.length === 2 ? short : short.padEnd(2, ' ');
                        const planetColor = PLANET_COLORS[key.toLowerCase()] || '#666';
                        return (
                          <div key={key} className="flex items-center justify-between bg-white/70 rounded-lg p-3 border border-saffron-200/50">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-md"
                                style={{ backgroundColor: planetColor }}
                              >
                                <span className="text-white font-bold text-xs">{abbreviation}</span>
                              </div>
                              <span className="text-saffron-600">=</span>
                            </div>
                            <span className="text-saffron-700 font-semibold">{PLANET_NAMES[key] || key}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Planet Meanings */}
                  <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-5 border border-saffron-200">
                    <h3 className="text-lg font-bold text-saffron-700 mb-4">Planet Meanings</h3>
                    <div className="space-y-3">
                      {Object.entries(PLANET_SHORT).map(([key, short]) => {
                        const abbreviation = short.length === 2 ? short : short.padEnd(2, ' ');
                        const planetColor = PLANET_COLORS[key.toLowerCase()] || '#666';
                        const meanings = {
                          sun: 'Authority, Leadership, Soul Purpose',
                          moon: 'Mind, Emotions, Mother',
                          mars: 'Energy, Courage, Action',
                          mercury: 'Communication, Intelligence, Business',
                          jupiter: 'Wisdom, Spirituality, Expansion',
                          venus: 'Love, Beauty, Relationships',
                          saturn: 'Discipline, Karma, Challenges',
                          rahu: 'Desires, Material Goals, Innovation',
                          ketu: 'Detachment, Spirituality, Past Life'
                        };
                        return (
                          <div key={key} className="bg-white/70 rounded-lg p-3 border border-saffron-200/50">
                            <div className="flex items-center gap-2 mb-1">
                              <div
                                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-md"
                                style={{ backgroundColor: planetColor }}
                              >
                                <span className="text-white font-bold text-xs">{abbreviation}</span>
                              </div>
                              <span className="font-semibold text-saffron-700">{PLANET_NAMES[key] || key}</span>
                            </div>
                            <p className="text-xs text-saffron-600 leading-relaxed ml-10">
                              {meanings[key.toLowerCase()] || meanings.sun}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Divisional charts when API provides them */}
              {data.divisionalCharts && (
                <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* D9 Navamsa */}
                  {data.divisionalCharts.d9 && (
                    <motion.div className="ks-dash-panel p-6">
                          <h2 className="text-2xl font-bold text-saffron-700 mb-4">Navamsa Chart (D9)</h2>
                      <p className="text-sm text-saffron-600 mb-4">Marriage & Relationships</p>
                      {(() => {
                        const d9Planets = {};
                        Object.entries(data.planets).forEach(([key, planet]) => {
                          d9Planets[key] = {
                            ...planet,
                            sign: planet.d9?.sign || planet.sign,
                            longitude: planet.d9?.longitude || planet.longitude
                          };
                        });
                        return (
                          <SouthIndianChart 
                            ascendant={data.divisionalCharts.d9?.lagna?.sign || data.lagna?.sign || SIGNS[0]}
                            chartData={d9Planets}
                          />
                        );
                      })()}
                    </motion.div>
                  )}

                  {/* D10 Dashmansh */}
                  {data.divisionalCharts.d10 && (
                    <motion.div className="ks-dash-panel p-6">
                      <h2 className="text-2xl font-bold text-saffron-700 mb-4">Dashmansh Chart (D10)</h2>
                      <p className="text-sm text-saffron-600 mb-4">Career & Profession</p>
                      {(() => {
                        const d10Planets = {};
                        Object.entries(data.planets).forEach(([key, planet]) => {
                          d10Planets[key] = {
                            ...planet,
                            sign: planet.d10?.sign || planet.sign,
                            longitude: planet.d10?.longitude || planet.longitude
                          };
                        });
                        return (
                          <SouthIndianChart 
                            ascendant={data.divisionalCharts.d10?.lagna?.sign || data.lagna?.sign || SIGNS[0]}
                            chartData={d10Planets}
                          />
                        );
                      })()}
                    </motion.div>
                  )}

                  {/* D7 Saptamsa */}
                  {data.divisionalCharts.d7 && (
                    <motion.div className="ks-dash-panel p-6">
                      <h2 className="text-2xl font-bold text-saffron-700 mb-4">Saptamsa Chart (D7)</h2>
                      <p className="text-sm text-saffron-600 mb-4">Children & Progeny</p>
                      {(() => {
                        const d7Planets = {};
                        Object.entries(data.planets).forEach(([key, planet]) => {
                          d7Planets[key] = {
                            ...planet,
                            sign: planet.d7?.sign || planet.sign,
                            longitude: planet.d7?.longitude || planet.longitude
                          };
                        });
                        return (
                          <SouthIndianChart 
                            ascendant={data.divisionalCharts.d7?.lagna?.sign || data.lagna?.sign || SIGNS[0]}
                            chartData={d7Planets}
                          />
                        );
                      })()}
                    </motion.div>
                  )}

                  {/* D12 Dwadashamsa */}
                  {data.divisionalCharts.d12 && (
                    <motion.div className="ks-dash-panel p-6">
                      <h2 className="text-2xl font-bold text-saffron-700 mb-4">Dwadashamsa Chart (D12)</h2>
                      <p className="text-sm text-saffron-600 mb-4">Parents & Ancestors</p>
                      {(() => {
                        const d12Planets = {};
                        Object.entries(data.planets).forEach(([key, planet]) => {
                          d12Planets[key] = {
                            ...planet,
                            sign: planet.d12?.sign || planet.sign,
                            longitude: planet.d12?.longitude || planet.longitude
                          };
                        });
                        return (
                          <SouthIndianChart 
                            ascendant={data.divisionalCharts.d12?.lagna?.sign || data.lagna?.sign || SIGNS[0]}
                            chartData={d12Planets}
                          />
                        );
                      })()}
                    </motion.div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}


          {/* Numerology Tab */}
          {activeTab === 'numerology' && data.numerology && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <NumerologySection numerology={data.numerology} itemVariants={itemVariants} birthData={birthData} isHindi={isHindi} />
            </motion.div>
          )}

          {activeTab === 'ask-question' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {effectiveUserId && effectiveMemberId ? (
                <AskKundliQuestionTab
                  userId={effectiveUserId}
                  profileId={effectiveMemberId}
                  userName={user?.name}
                  userEmail={user?.email}
                  memberName={birthData?.name}
                  itemVariants={itemVariants}
                  prefilledQuestion={birthData?.question}
                />
              ) : (
                <div className="ks-dash-panel p-6 text-amber-900 text-center">
                  {isHindi
                    ? 'Kripya pehle profile select karein — phir yahan apna sawaal pooch sakte hain.'
                    : 'Please select a profile first — then you can ask your question here.'}
                </div>
              )}
            </motion.div>
          )}

          {/* Mahadasha Tab */}
          {activeTab === 'mahadasha' && data.mahadasha && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Current period — always visible */}
              {data.mahadasha.current && (
                <motion.div variants={itemVariants} className="ks-dash-panel p-6 mb-4">
                  <h2 className="text-2xl font-bold text-saffron-700 mb-4">⏳ Current Dasha Period</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                      <div className="text-sm font-semibold text-saffron-700 mb-1">Mahadasha (Major Period)</div>
                      <div className="text-xl font-bold text-saffron-800">{data.mahadasha.current.lord}</div>
                      {data.mahadasha.current.start && <div className="text-xs text-saffron-600 mt-1">{data.mahadasha.current.start} → {data.mahadasha.current.end}</div>}
                    </div>
                    {data.mahadasha.current.antardasha && (
                      <div className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                        <div className="text-sm font-semibold text-saffron-700 mb-1">Antardasha (Sub-Period)</div>
                        <div className="text-xl font-bold text-saffron-800">{data.mahadasha.current.antardasha}</div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-4">Mahadasha Timeline</h2>
                <MahadashaTimeline mahadasha={data.mahadasha} />
              </motion.div>
            </motion.div>
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && data.insights && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              {/* Personality Insights */}
              <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                <h2 className="text-2xl font-bold text-saffron-700 mb-4">Personality Insights</h2>
                <div className="space-y-4">
                  {data.insights.map((insight, index) => (
                    <InsightCard key={index} insight={insight} index={index} data={data} />
                  ))}
                </div>
              </motion.div>

              {/* Planetary Strengths */}
              {data.planets && (
                <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                  <h2 className="text-2xl font-bold text-saffron-700 mb-4">Planetary Strengths & Positions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(data.planets).map(([key, planet]) => {
                      const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                      const planetSignIndex = SIGNS.indexOf(planet.sign);
                      const houseNumber = ((planetSignIndex - ascendantIndex + 12) % 12) + 1;
                      
                      // Determine strength based on house
                      const isAngular = [1, 4, 7, 10].includes(houseNumber);
                      const isTrinal = [5, 9].includes(houseNumber);
                      const isUpachaya = [3, 6, 10, 11].includes(houseNumber);
                      const isDusthana = [6, 8, 12].includes(houseNumber);
                      
                      let strength = 'Moderate';
                      let strengthColor = 'text-saffron-600';
                      if (isAngular || isTrinal) {
                        strength = 'Strong';
                        strengthColor = 'text-green-600';
                      } else if (isDusthana) {
                        strength = 'Challenging';
                        strengthColor = 'text-orange-600';
                      }
                      
                      return (
                        <div key={key} className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-4 border border-saffron-200">
                          <div className="flex items-center gap-3 mb-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center shadow-md"
                              style={{ backgroundColor: PLANET_COLORS[key] || '#666' }}
                            >
                              <span className="text-white font-bold text-xs">{PLANET_SHORT[PLANET_NAMES[key]]}</span>
                            </div>
                            <div>
                              <div className="font-bold text-saffron-800">{PLANET_NAMES[key] || key}</div>
                              <div className={`text-xs font-semibold ${strengthColor}`}>{strength}</div>
                            </div>
                          </div>
                          <div className="text-xs text-saffron-700 space-y-1">
                            <div><span className="font-semibold">House:</span> {getHouseName(houseNumber)} ({planet.sign})</div>
                            <div><span className="font-semibold">Position:</span> {planet.deg}°</div>
                            <div><span className="font-semibold">Nakshatra:</span> {getNakshatraName(planet.nakshatra)}</div>
                            {isAngular && <div className="text-green-600 text-xs mt-1">✓ Angular House (Powerful)</div>}
                            {isTrinal && <div className="text-green-600 text-xs mt-1">✓ Trinal House (Auspicious)</div>}
                            {isUpachaya && <div className="text-blue-600 text-xs mt-1">↑ Upachaya House (Growth)</div>}
                            {isDusthana && <div className="text-orange-600 text-xs mt-1">⚠ Dusthana House (Challenging)</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* House Lordships */}
              {data.lagna && (
                <motion.div variants={itemVariants} className="ks-dash-panel p-6">
                  <h2 className="text-2xl font-bold text-saffron-700 mb-4">House Lordships</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {Array.from({ length: 12 }, (_, i) => {
                      const houseNumber = i + 1;
                      const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                      const signIndex = (ascendantIndex + i) % 12;
                      const sign = SIGNS[signIndex];
                      
                      // Find lord of this sign
                      const signLords = {
                        'Aries': 'Mars', 'Taurus': 'Venus', 'Gemini': 'Mercury',
                        'Cancer': 'Moon', 'Leo': 'Sun', 'Virgo': 'Mercury',
                        'Libra': 'Venus', 'Scorpio': 'Mars', 'Sagittarius': 'Jupiter',
                        'Capricorn': 'Saturn', 'Aquarius': 'Saturn', 'Pisces': 'Jupiter'
                      };
                      
                      const lord = signLords[sign];
                      const lordKey = lord.toLowerCase();
                      
                      return (
                        <div key={houseNumber} className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-lg p-3 border border-saffron-200 text-center">
                          <div className="font-bold text-saffron-800 mb-1 text-sm">{getHouseName(houseNumber)}</div>
                          <div className="text-xs text-saffron-600 mb-2">{sign}</div>
                          <div
                            className="w-8 h-8 rounded-full mx-auto flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: PLANET_COLORS[lordKey] || '#666' }}
                          >
                            <span className="text-white font-bold text-xs">{PLANET_SHORT[lord]}</span>
                          </div>
                          <div className="text-xs text-saffron-700 mt-1">{PLANET_NAMES[lordKey] || lordKey}</div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Karmic Insights Tab - Unique Feature */}
          {activeTab === 'karmic' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-6"
            >
              {/* Karmic Preview — always visible */}
              {data.nakshatra && (
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-50 rounded-2xl shadow-xl p-6 border-2 border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🌀</span>
                    <h2 className="text-2xl font-bold text-purple-700">Karmic Insights</h2>
                  </div>
                  <div className="rounded-2xl bg-white p-5 border border-purple-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <p className="text-purple-700 leading-relaxed mb-3" style={{ fontStyle:'italic' }}>
                      🔮 Aap apne past life se ek karmic pattern lekar aaye hain... jo aaj ke relationships ko affect karta hai...
                    </p>
                    <p className="text-purple-600 text-sm leading-relaxed mb-4">
                      Aapka {data.nakshatra?.name} Nakshatra ek specific karmic lesson indicate karta hai — jo is janam mein complete karna aapka dharma hai.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-4 border border-purple-200">
                        <div className="font-bold text-purple-800 mb-2">{data.nakshatra?.name} Nakshatra - Karmic Lesson</div>
                        <p className="text-sm text-purple-700">
                          Born under {data.nakshatra?.name} Nakshatra, you carry the karmic imprint of spiritual growth and transformation.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                          <div className="font-semibold text-purple-800 mb-1">Karmic Debt</div>
                          <p className="text-xs text-purple-700">You may need to resolve relationships and emotional patterns from past lives.</p>
                        </div>
                        <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                          <div className="font-semibold text-indigo-800 mb-1">Karmic Gift</div>
                          <p className="text-xs text-indigo-700">You possess natural talents and wisdom from past lives.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Hidden Talents & Spiritual Path */}
              {data.planets && data.lagna && (
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50 rounded-2xl shadow-xl p-6 border-2 border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">✨</span>
                    <h2 className="text-2xl font-bold text-amber-700">Hidden Talents & Abilities</h2>
                  </div>
                  <div className="rounded-2xl bg-white p-5 border border-amber-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <p className="text-saffron-700 leading-relaxed mb-4">
                      Your chart reveals unique talents that may be hidden or underdeveloped. These abilities can be your path to success and fulfillment.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(() => {
                        const talents = [];
                        const ascendantIndex = SIGNS.indexOf(data.lagna.sign);
                        
                        // Analyze planets in specific houses for talents
                        Object.entries(data.planets).forEach(([key, planet]) => {
                          const planetSignIndex = SIGNS.indexOf(planet.sign);
                          const houseNumber = ((planetSignIndex - ascendantIndex + 12) % 12) + 1;
                          
                          if (houseNumber === 5) {
                            talents.push({
                              planet: PLANET_NAMES[key] || key,
                              talent: 'Creative Arts, Teaching, Writing',
                              icon: '🎨'
                            });
                          } else if (houseNumber === 9) {
                            talents.push({
                              planet: PLANET_NAMES[key] || key,
                              talent: 'Spirituality, Philosophy, Higher Learning',
                              icon: '📚'
                            });
                          } else if (houseNumber === 10) {
                            talents.push({
                              planet: PLANET_NAMES[key] || key,
                              talent: 'Leadership, Public Speaking, Authority',
                              icon: '👑'
                            });
                          }
                        });
                        
                        if (talents.length === 0) {
                          talents.push({
                            planet: 'Chart Analysis',
                            talent: 'Your chart shows potential in multiple areas. Focus on developing skills related to your Lagna and Moon sign.',
                            icon: '🌟'
                          });
                        }
                        
                        return talents.map((talent, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg p-4 border border-amber-200">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">{talent.icon}</span>
                              <span className="font-bold text-amber-800">{talent.planet}</span>
                            </div>
                            <p className="text-sm text-amber-700">{talent.talent}</p>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Life Purpose & Dharma */}
              {data.lagna && data.chandra_rashi && (
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-50 rounded-2xl shadow-xl p-6 border-2 border-teal-200">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🎯</span>
                    <h2 className="text-2xl font-bold text-teal-700">Life Purpose & Dharma</h2>
                  </div>
                  <div className="rounded-2xl bg-white p-5 border border-teal-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <p className="text-saffron-700 leading-relaxed mb-4">
                      Your Lagna and Moon sign reveal your life purpose and dharma. Understanding this helps you align with your true calling.
                    </p>
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-teal-100 to-cyan-100 rounded-lg p-4 border border-teal-200">
                        <div className="font-bold text-teal-800 mb-2">Lagna Purpose</div>
                        <p className="text-sm text-teal-700">
                          Your {data.lagna.sign} Lagna indicates your primary life purpose and karmic direction in this lifetime.
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-cyan-100 to-teal-100 rounded-lg p-4 border border-cyan-200">
                        <div className="font-bold text-cyan-800 mb-2">Moon Purpose</div>
                        <p className="text-sm text-cyan-700">
                          Your {data.chandra_rashi.sign} Moon reveals your emotional nature and inner needs.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Spiritual Path */}
              {data.nakshatra && (
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-violet-50 via-purple-50 to-violet-50 rounded-2xl shadow-xl p-6 border-2 border-violet-200">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🕉️</span>
                    <h2 className="text-2xl font-bold text-violet-700">{t('dashboard.spiritualPath', 'Spiritual Path & Evolution')}</h2>
                  </div>
                  <div className="rounded-2xl bg-white p-5 border border-violet-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <p className="text-saffron-700 leading-relaxed mb-4">
                      {t('dashboard.spiritualDescription', 'Your chart reveals your spiritual evolution path and practices that will accelerate your growth.')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg p-4 border border-violet-200">
                        <div className="font-bold text-violet-800 mb-2">{t('dashboard.recommendedPractice', 'Recommended Practice')}</div>
                        <p className="text-sm text-violet-700">
                          {t('dashboard.practiceDesc', 'Meditation, chanting, and service to others will accelerate your spiritual growth. Focus on developing inner peace and detachment.')}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-100 to-violet-100 rounded-lg p-4 border border-purple-200">
                        <div className="font-bold text-purple-800 mb-2">{t('dashboard.spiritualGoal', 'Spiritual Goal')}</div>
                        <p className="text-sm text-purple-700">
                          {t('dashboard.goalDesc', 'Your ultimate goal is to transcend material attachments and realize your true nature. This lifetime offers opportunities for significant spiritual advancement.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

        </motion.div>
      </div>

      {/* Floating CTA — desktop/tablet only (mobile uses sticky bar + hero card) */}
      {effectiveUserId && effectiveMemberId && activeTab !== 'ask-question' && (
        <button
          type="button"
          className="hidden lg:flex ks-fab-shastra-ai fixed z-[55] max-w-[min(calc(100vw-1.5rem),14rem)] flex-col items-end gap-0.5 rounded-full border border-amber-200/50 bg-gradient-to-br from-[#78350f] via-[#854a0f] to-[#92400e] px-3 py-2 text-right text-[#fffbeb] shadow-[0_4px_14px_rgba(0,0,0,.14)] transition hover:shadow-[0_6px_18px_rgba(0,0,0,.18)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fff7ed] sm:px-3.5 sm:py-2"
          style={{
            bottom: 'max(1rem, env(safe-area-inset-bottom, 12px))',
            right: 'max(0.75rem, env(safe-area-inset-right, 12px))',
          }}
          onClick={() => {
            setActiveTab('ask-question');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label={`Open Shastra AI — personalised answer, ${shastraPriceText}`}
        >
          <span className="text-[0.8125rem] font-medium leading-tight tracking-tight sm:text-sm">
            <span aria-hidden className="mr-0.5">
              ✨
            </span>
            Ask Shastra AI
          </span>
          <span className="text-[0.62rem] font-normal leading-none text-amber-100/85">
            Personalised answer
          </span>
        </button>
      )}

      <ShastraProfileQuestionModal
        open={shastraProfileModalOpen}
        question={profileQuestionTrim}
        onClose={() => {
          setShastraProfileModalOpen(false);
          try {
            if (effectiveMemberId) {
              sessionStorage.setItem(`ks_shastra_welcome_dismissed_${effectiveMemberId}`, '1');
            }
          } catch (_) {
            /* ignore */
          }
        }}
        onUnlock={() => {
          setShastraProfileModalOpen(false);
          setActiveTab('ask-question');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        freeQuestionAvailable={shastraFreeEligible}
      />
    </div>
  );
}

// Advanced Numerology Functions
const calculateExpressionNumber = (fullName) => {
  const values = { A:1, B:2, C:3, D:4, E:5, F:6, G:7, H:8, I:9, J:1, K:2, L:3, M:4, N:5, O:6, P:7, Q:8, R:9, S:1, T:2, U:3, V:4, W:5, X:6, Y:7, Z:8 };
  const sum = fullName.toUpperCase().replace(/[^A-Z]/g, '').split('').reduce((acc, char) => acc + (values[char] || 0), 0);
  return reduceToSingle(sum);
};

const calculateSoulUrge = (fullName) => {
  const vowels = 'AEIOU';
  const values = { A:1, E:5, I:9, O:6, U:3 };
  const sum = fullName.toUpperCase().split('').filter(char => vowels.includes(char)).reduce((acc, char) => acc + (values[char] || 0), 0);
  return reduceToSingle(sum);
};

const calculatePersonalityNumber = (fullName) => {
  const vowels = 'AEIOU';
  const values = { B:2, C:3, D:4, F:6, G:7, H:8, J:1, K:2, L:3, M:4, N:5, P:7, Q:8, R:9, S:1, T:2, V:4, W:5, X:6, Y:7, Z:8 };
  const sum = fullName.toUpperCase().split('').filter(char => !vowels.includes(char) && char.match(/[A-Z]/)).reduce((acc, char) => acc + (values[char] || 0), 0);
  return reduceToSingle(sum);
};

const reduceToSingle = (num) => {
  if (num === 11 || num === 22 || num === 33) return num;
  while (num > 9) {
    num = num.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
  }
  return num;
};

const calculateKarmicDebt = (birthDate) => {
  const [year, month, day] = birthDate.split('-').map(Number);
  const karmicDebtNumbers = [13, 14, 16, 19];
  const found = [];
  
  if ([4, 13].includes(day) || [4, 13].includes(month)) found.push(13);
  if ([5, 14].includes(day) || [5, 14].includes(month)) found.push(14);
  if ([7, 16].includes(day) || [7, 16].includes(month)) found.push(16);
  if ([1, 10, 19].includes(day) || [1, 10].includes(month)) found.push(19);
  
  return found;
};

const calculateKarmicLessons = (fullName) => {
  const values = { A:1, B:2, C:3, D:4, E:5, F:6, G:7, H:8, I:9, J:1, K:2, L:3, M:4, N:5, O:6, P:7, Q:8, R:9, S:1, T:2, U:3, V:4, W:5, X:6, Y:7, Z:8 };
  const present = new Set();
  fullName.toUpperCase().replace(/[^A-Z]/g, '').split('').forEach(char => {
    if (values[char]) present.add(values[char]);
  });
  
  const missing = [];
  for (let i = 1; i <= 9; i++) {
    if (!present.has(i)) missing.push(i);
  }
  return missing;
};

const calculatePersonalYear = (birthMonth, birthDay, currentYear) => {
  const sum = birthMonth + birthDay + currentYear;
  return reduceToSingle(sum);
};

const calculatePersonalMonth = (personalYear, currentMonth) => {
  return reduceToSingle(personalYear + currentMonth);
};

const calculatePersonalDay = (personalMonth, currentDay) => {
  return reduceToSingle(personalMonth + currentDay);
};

// Numerology Section Component
function NumerologySection({ numerology, itemVariants, birthData, isHindi = false }) {
  const { t } = useTranslation();
  
  // Safety check
  if (!numerology || !numerology.lifePathNumber || !numerology.birthDayNumber) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
        <p className="text-red-700 font-medium">
          Numerology data is not available.
        </p>
      </div>
    );
  }

  // Calculate additional numerology if birthData is available
  const currentDate = new Date();
  const [birthYear, birthMonth, birthDay] = birthData?.date ? birthData.date.split('-').map(Number) : [1990, 1, 1];
  const fullName = birthData?.name || "Sample Name";
  
  const expressionNumber = calculateExpressionNumber(fullName);
  const soulUrge = calculateSoulUrge(fullName);
  const personalityNumber = calculatePersonalityNumber(fullName);
  const karmicDebt = calculateKarmicDebt(birthData?.date || '1990-01-01');
  const karmicLessons = calculateKarmicLessons(fullName);
  const personalYear = calculatePersonalYear(birthMonth, birthDay, currentDate.getFullYear());
  const personalMonth = calculatePersonalMonth(personalYear, currentDate.getMonth() + 1);
  const personalDay = calculatePersonalDay(personalMonth, currentDate.getDate());
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const getNumberColor = (num) => {
    const colors = {
      1: '#FF6B35', 2: '#F79D3E', 3: '#FFB627', 4: '#6A994E', 
      5: '#386FA4', 6: '#59A5D8', 7: '#7209B7', 8: '#A663CC', 9: '#F72585',
      11: '#FF8500', 22: '#023E8A', 33: '#7209B7'
    };
    return colors[num] || '#6366F1';
  };

  const getNumberDescription = (num, type) => {
    const descriptions = {
      lifePath: {
        1: "Leader, Pioneer, Independent",
        2: "Diplomat, Peacemaker, Cooperative", 
        3: "Creative, Expressive, Optimistic",
        4: "Practical, Hardworking, Loyal",
        5: "Freedom-loving, Adventurous, Dynamic",
        6: "Nurturing, Responsible, Caring",
        7: "Spiritual, Analytical, Mysterious",
        8: "Ambitious, Material Success, Powerful",
        9: "Humanitarian, Wise, Generous",
        11: "Intuitive, Inspirational, Enlightened",
        22: "Master Builder, Visionary, Practical Idealist",
        33: "Master Teacher, Spiritual Guide"
      },
      expression: {
        1: "Natural leader with pioneering spirit",
        2: "Diplomatic peacemaker with cooperation skills",
        3: "Creative artist with communication gifts",
        4: "Reliable builder with practical wisdom",
        5: "Dynamic communicator with versatile talents",
        6: "Nurturing healer with service orientation",
        7: "Spiritual seeker with analytical mind",
        8: "Executive leader with business acumen",
        9: "Universal humanitarian with broad vision"
      }
    };
    return descriptions[type]?.[num] || `Powerful ${type} energy`;
  };

  const getDetailedDescription = (num, type) => {
    const detailed = {
      lifePath: {
        1: "You are born to lead and pioneer new paths. Independence and originality are your gifts. You have strong willpower and the ability to initiate projects. Career success comes through leadership roles and entrepreneurship.",
        2: "You are the peacemaker and diplomat. Cooperation and partnership are essential to your success. You excel in supportive roles and team environments. Relationships and harmony are central to your life purpose.",
        3: "You are blessed with creative expression and communication gifts. Art, writing, speaking, and entertainment are your natural domains. Joy, optimism, and inspiring others are your life themes.",
        4: "You are the reliable foundation builder. Hard work, discipline, and practical solutions are your strengths. You create lasting structures and bring stability to chaotic situations.",
        5: "Freedom and adventure drive your soul. You need variety, travel, and new experiences. Change is your ally, and you inspire others to break free from limitations.",
        6: "You are the nurturer and caregiver. Home, family, and healing others fulfill your purpose. Responsibility and service to community are your calling.",
        7: "You are on a spiritual quest for truth and wisdom. Analysis, research, and mystical studies attract you. Solitude and introspection are necessary for your growth.",
        8: "Material mastery and worldly success are your goals. Business, finance, and positions of authority suit you. You have the power to achieve great material accomplishments.",
        9: "You are the humanitarian with a universal perspective. Service to humanity and sharing wisdom are your purposes. Completion and letting go are important lessons."
      },
      mulank: {
        1: "Independent and strong-willed, you prefer to lead rather than follow. You have natural authority and confidence that attracts others to you.",
        2: "Gentle and cooperative, you work best in partnerships. You are sensitive to others' needs and excel in supportive roles.",
        3: "Creative and expressive, you bring joy wherever you go. Communication and artistic pursuits come naturally to you.",
        4: "Practical and reliable, you are the backbone others depend on. You build things to last and value stability above all.",
        5: "Dynamic and freedom-loving, you crave variety and adventure. Routine bores you, and you need constant mental stimulation.",
        6: "Caring and responsible, you naturally take care of others. Home and family are extremely important to your happiness.",
        7: "Analytical and spiritual, you prefer depth over surface interactions. You need quiet time to recharge and reflect.",
        8: "Ambitious and business-minded, you focus on material achievement. Power and recognition motivate your actions.",
        9: "Compassionate and wise, you see the bigger picture. You're drawn to humanitarian causes and helping others."
      }
    };
    return detailed[type]?.[num] || "Your number carries unique spiritual significance and life lessons.";
  };
  
  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      {/* Auspicious Header */}
      <motion.div variants={itemVariants} className="relative bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700 rounded-3xl p-8 mb-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 text-8xl">🕉️</div>
          <div className="absolute bottom-0 right-0 text-8xl">🔢</div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-9xl">✨</div>
        </div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="text-5xl">🔢</span>
            <h2 className="text-4xl font-bold text-white">
              Sacred Numerology
          </h2>
            <span className="text-5xl">✨</span>
          </div>
          <p className="text-white text-opacity-95 text-lg font-medium">
            {isHindi
              ? 'अंकशास्त्र विश्लेषण • Divine Number Science & Soul Blueprint'
              : 'Divine Number Science & Soul Blueprint'}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-white text-opacity-90">
            <span>🌟</span>
            <span>Chaldean System</span>
            <span>•</span>
            <span>Ancient Wisdom</span>
            <span>🌟</span>
        </div>
        </div>
      </motion.div>

      {/* Core Numbers Grid */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50 rounded-3xl p-8 mb-8 border-2 border-orange-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-orange-800 mb-2">
            🎯 Core Numbers
          </h3>
          <p className="text-orange-700">
            Your primary numerological influences
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Life Path Number */}
          <div className="bg-white rounded-2xl p-6 border-3 border-purple-300 text-center shadow-xl transform hover:scale-105 transition-all">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(numerology.lifePathNumber.number) }}
            >
              {numerology.lifePathNumber.number}
            </div>
            <h4 className="font-bold text-purple-800 mb-2">Life Path</h4>
            {isHindi && <p className="text-xs text-purple-600 font-semibold mb-2">भाग्यांक (Bhagyank)</p>}
            <p className="text-xs text-purple-700">{getNumberDescription(numerology.lifePathNumber.number, 'lifePath')}</p>
          </div>
          
          {/* Mulank (Birthday Number) */}
          <div className="bg-white rounded-2xl p-6 border-3 border-orange-300 text-center shadow-xl transform hover:scale-105 transition-all">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(numerology.birthDayNumber.number) }}
            >
              {numerology.birthDayNumber.number}
            </div>
            <h4 className="font-bold text-orange-800 mb-2">Mulank</h4>
            {isHindi && <p className="text-xs text-orange-600 font-semibold mb-2">मूलांक (Birthday Number)</p>}
            <p className="text-xs text-orange-700">Core personality driver</p>
          </div>
          
          {/* Expression Number */}
          <div className="bg-white rounded-2xl p-6 border-3 border-blue-300 text-center shadow-xl transform hover:scale-105 transition-all">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(expressionNumber) }}
            >
              {expressionNumber}
            </div>
            <h4 className="font-bold text-blue-800 mb-2">Expression</h4>
            {isHindi && <p className="text-xs text-blue-600 font-semibold mb-2">नाम अंक (Name Number)</p>}
            <p className="text-xs text-blue-700">{getNumberDescription(expressionNumber, 'expression')}</p>
          </div>
          
          {/* Soul Urge */}
          <div className="bg-white rounded-2xl p-6 border-3 border-pink-300 text-center shadow-xl transform hover:scale-105 transition-all">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(soulUrge) }}
            >
              {soulUrge}
            </div>
            <h4 className="font-bold text-pink-800 mb-2">Soul Urge</h4>
            {isHindi && <p className="text-xs text-pink-600 font-semibold mb-2">आत्मा की इच्छा (Soul Desire)</p>}
            <p className="text-xs text-pink-700">Inner motivation & desires</p>
          </div>
          
          {/* Personality Number */}
          <div className="bg-white rounded-2xl p-6 border-3 border-green-300 text-center shadow-xl transform hover:scale-105 transition-all">
            <div 
              className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(personalityNumber) }}
            >
              {personalityNumber}
            </div>
            <h4 className="font-bold text-green-800 mb-2">Personality</h4>
            {isHindi && <p className="text-xs text-green-600 font-semibold mb-2">व्यक्तित्व अंक</p>}
            <p className="text-xs text-green-700">How others perceive you</p>
          </div>
        </div>
      </motion.div>

      {/* Karmic Analysis */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-red-50 via-rose-50 to-red-50 rounded-3xl p-8 mb-8 border-2 border-red-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-red-800 mb-2">
            🌀 Karmic Analysis
            </h3>
          <p className="text-red-700">
            Past life influences & soul lessons
              </p>
            </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Karmic Debt Numbers */}
          <div className="bg-white rounded-2xl p-6 border-2 border-red-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">⚖️</span>
              <h4 className="text-xl font-bold text-red-800">Karmic Debt Numbers</h4>
            </div>
            {karmicDebt.length > 0 ? (
              <div className="space-y-3">
                {karmicDebt.map((num) => (
                  <div key={num} className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-red-500 text-white font-bold flex items-center justify-center text-lg">
                        {num}
                      </div>
                      <div>
                        <p className="font-bold text-red-800">Karmic Debt {num}</p>
                        <p className="text-xs text-red-600">
                          {num === 13 && "Hard work & focus needed"}
                          {num === 14 && "Freedom misused in past life"}
                          {num === 16 && "Spiritual awakening required"}
                          {num === 19 && "Learn humility & service"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-green-700 font-semibold">No Major Karmic Debts</p>
                <p className="text-xs text-green-600">Clear karmic path in this lifetime</p>
              </div>
            )}
          </div>
          
          {/* Karmic Lessons */}
          <div className="bg-white rounded-2xl p-6 border-2 border-orange-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">📚</span>
              <h4 className="text-xl font-bold text-orange-800">Karmic Lessons</h4>
            </div>
            {karmicLessons.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-orange-700 mb-3">Numbers to develop:</p>
                <div className="flex flex-wrap gap-2">
                  {karmicLessons.map((num) => (
                    <div key={num} className="bg-orange-100 rounded-lg px-3 py-2 border border-orange-200">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-orange-500 text-white font-bold flex items-center justify-center text-sm">
                          {num}
                        </div>
                        <span className="text-xs font-semibold text-orange-800">
                          {num === 1 && "Leadership"}
                          {num === 2 && "Cooperation"}
                          {num === 3 && "Creativity"}
                          {num === 4 && "Discipline"}
                          {num === 5 && "Freedom"}
                          {num === 6 && "Responsibility"}
                          {num === 7 && "Spirituality"}
                          {num === 8 && "Material mastery"}
                          {num === 9 && "Wisdom"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="text-4xl mb-2">🌟</div>
                <p className="text-green-700 font-semibold">Complete Numerological Set</p>
                <p className="text-xs text-green-600">All numbers present in your name</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Personal Cycles */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-indigo-50 via-purple-50 to-indigo-50 rounded-3xl p-8 mb-8 border-2 border-indigo-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-indigo-800 mb-2">
            🔄 Personal Cycles
          </h3>
          <p className="text-indigo-700">
            Current numerological influences
            </p>
          </div>
          
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Personal Year */}
          <div className="bg-white rounded-2xl p-6 border-2 border-indigo-200 text-center shadow-lg">
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-4xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(personalYear) }}
            >
              {personalYear}
            </div>
            <h4 className="text-xl font-bold text-indigo-800 mb-2">Personal Year</h4>
            <p className="text-sm text-indigo-600 font-semibold mb-2">{currentDate.getFullYear()}</p>
            <p className="text-xs text-indigo-700">{getNumberDescription(personalYear, 'lifePath')}</p>
            </div>
          
          {/* Personal Month */}
          <div className="bg-white rounded-2xl p-6 border-2 border-purple-200 text-center shadow-lg">
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-4xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(personalMonth) }}
            >
              {personalMonth}
            </div>
            <h4 className="text-xl font-bold text-purple-800 mb-2">Personal Month</h4>
            <p className="text-sm text-purple-600 font-semibold mb-2">
              {currentDate.toLocaleString('en', { month: 'long' })}
            </p>
            <p className="text-xs text-purple-700">Monthly theme & focus</p>
          </div>
          
          {/* Personal Day */}
          <div className="bg-white rounded-2xl p-6 border-2 border-pink-200 text-center shadow-lg">
            <div 
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-4xl font-bold text-white shadow-lg"
              style={{ backgroundColor: getNumberColor(personalDay) }}
            >
              {personalDay}
            </div>
            <h4 className="text-xl font-bold text-pink-800 mb-2">Personal Day</h4>
            <p className="text-sm text-pink-600 font-semibold mb-2">Today</p>
            <p className="text-xs text-pink-700">Daily energy & opportunities</p>
          </div>
        </div>
      </motion.div>
      
      {/* Lucky Elements */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50 rounded-3xl p-8 border-2 border-amber-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-amber-800 mb-2">
            🍀 Sacred Lucky Elements
            </h3>
          <p className="text-amber-700">
            Divine blessings & auspicious influences
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Lucky Color */}
          <div className="bg-white rounded-2xl p-6 border-2 border-amber-200 text-center shadow-lg">
            <div className="text-5xl mb-4">🎨</div>
            <h4 className="font-bold text-amber-800 mb-3 text-lg">Lucky Color</h4>
            <p className="text-xl font-bold text-amber-900">
              {numerology.luckyColor}
            </p>
            <p className="text-xs text-amber-600 mt-2">Wear for positive energy</p>
          </div>
          
          {/* Lucky Gemstone */}
          <div className="bg-white rounded-2xl p-6 border-2 border-amber-200 text-center shadow-lg">
            <div className="text-5xl mb-4">💎</div>
            <h4 className="font-bold text-amber-800 mb-3 text-lg">Lucky Gemstone</h4>
            <p className="text-3xl font-bold text-amber-900 mb-3">
              {numerology.luckyGemstone}
            </p>
            <p className="text-xs text-amber-600">Sacred stone for prosperity</p>
          </div>
          
          {/* Lucky Days */}
          <div className="bg-white rounded-2xl p-6 border-2 border-amber-200 text-center shadow-lg">
            <div className="text-5xl mb-4">📅</div>
            <h4 className="font-bold text-amber-800 mb-3 text-lg">Lucky Days</h4>
            <div className="space-y-2">
              {numerology.luckyDays.map((day, idx) => (
                <div 
                  key={idx}
                  className="px-4 py-2 rounded-xl font-bold text-amber-900 border-2 border-amber-300"
                  style={{ backgroundColor: numerology.primaryNumber.color + '30' }}
                >
                  {day}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Detailed Number Explanations */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 rounded-3xl p-8 mb-8 border-2 border-blue-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-blue-800 mb-2">
            📖 Detailed Number Meanings
          </h3>
          <p className="text-blue-700">
            Deep insights into your sacred numbers
          </p>
        </div>
        
        <div className="space-y-6">
          {/* Life Path Description */}
          <div className="bg-white rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(numerology.lifePathNumber.number) }}
              >
                {numerology.lifePathNumber.number}
              </div>
              <div>
                <h4 className="text-xl font-bold text-purple-800">Life Path {numerology.lifePathNumber.number}</h4>
                {isHindi && <p className="text-sm text-purple-600 font-semibold">भाग्यांक (Bhagyank) - Soul's Journey</p>}
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {getDetailedDescription(numerology.lifePathNumber.number, 'lifePath')}
            </p>
          </div>

          {/* Mulank Description */}
          <div className="bg-white rounded-2xl p-6 border-2 border-orange-200 shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(numerology.birthDayNumber.number) }}
              >
                {numerology.birthDayNumber.number}
              </div>
              <div>
                <h4 className="text-xl font-bold text-orange-800">Mulank {numerology.birthDayNumber.number}</h4>
                {isHindi && <p className="text-sm text-orange-600 font-semibold">मूलांक (Birthday Number) - Core Nature</p>}
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              {getDetailedDescription(numerology.birthDayNumber.number, 'mulank')}
            </p>
                </div>

          {/* Expression Number Description */}
          <div className="bg-white rounded-2xl p-6 border-2 border-blue-200 shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(expressionNumber) }}
              >
                {expressionNumber}
              </div>
              <div>
                <h4 className="text-xl font-bold text-blue-800">Expression {expressionNumber}</h4>
                {isHindi && <p className="text-sm text-blue-600 font-semibold">नाम अंक (Destiny Number) - Life Mission</p>}
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Your Expression Number {expressionNumber} reveals your life's mission and the talents you're meant to develop and share with the world. This number shows your ultimate potential and the legacy you're here to create.
            </p>
          </div>

          {/* Soul Urge Description */}
          <div className="bg-white rounded-2xl p-6 border-2 border-pink-200 shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(soulUrge) }}
              >
                {soulUrge}
              </div>
              <div>
                <h4 className="text-xl font-bold text-pink-800">Soul Urge {soulUrge}</h4>
                {isHindi && <p className="text-sm text-pink-600 font-semibold">आत्मा की इच्छा (Heart's Desire) - Inner Motivation</p>}
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Your Soul Urge Number {soulUrge} represents what your heart truly desires and what motivates you from within. This is your inner calling and what brings you the deepest satisfaction and fulfillment.
            </p>
          </div>

          {/* Personality Number Description */}
          <div className="bg-white rounded-2xl p-6 border-2 border-green-200 shadow-lg">
            <div className="flex items-center gap-4 mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(personalityNumber) }}
              >
                {personalityNumber}
              </div>
              <div>
                <h4 className="text-xl font-bold text-green-800">Personality {personalityNumber}</h4>
                {isHindi && <p className="text-sm text-green-600 font-semibold">व्यक्तित्व अंक (Outer Self) - First Impression</p>}
              </div>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Your Personality Number {personalityNumber} reveals how others perceive you and the first impression you make. This is the mask you wear in public and how you present yourself to the world.
            </p>
          </div>
        </div>
            </motion.div>

      {/* Current Cycles Explanation */}
      <motion.div variants={itemVariants} className="bg-gradient-to-br from-violet-50 via-purple-50 to-violet-50 rounded-3xl p-8 mb-8 border-2 border-violet-300 shadow-xl">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-violet-800 mb-2">
            📅 Current Cycle Guidance
          </h3>
          <p className="text-violet-700">
            How to make the most of your current numbers
          </p>
            </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Personal Year Guidance */}
          <div className="bg-white rounded-2xl p-6 border-2 border-indigo-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(personalYear) }}
              >
                {personalYear}
        </div>
              <h4 className="text-lg font-bold text-indigo-800">Personal Year {personalYear}</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              {personalYear === 1 && "New beginnings year. Start fresh projects, take initiative, and be independent. Leadership opportunities will arise."}
              {personalYear === 2 && "Cooperation year. Focus on relationships, partnerships, and teamwork. Patience and diplomacy are key."}
              {personalYear === 3 && "Creative expression year. Share your talents, communicate, and enjoy social activities. Joy and optimism flow."}
              {personalYear === 4 && "Hard work year. Build foundations, organize, and focus on practical matters. Discipline brings rewards."}
              {personalYear === 5 && "Freedom and change year. Travel, explore new opportunities, and embrace variety. Adventure awaits."}
              {personalYear === 6 && "Responsibility year. Focus on home, family, and healing relationships. Service to others is highlighted."}
              {personalYear === 7 && "Spiritual development year. Study, research, and seek inner wisdom. Introspection and meditation are beneficial."}
              {personalYear === 8 && "Material achievement year. Focus on career advancement and financial goals. Authority and recognition come."}
              {personalYear === 9 && "Completion year. Finish old projects, let go of what no longer serves, and prepare for new cycles."}
            </p>
          </div>

          {/* Personal Month Guidance */}
          <div className="bg-white rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(personalMonth) }}
              >
                {personalMonth}
              </div>
              <h4 className="text-lg font-bold text-purple-800">Personal Month {personalMonth}</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              This month's energy supports {personalMonth === 1 ? "new beginnings and taking initiative" : personalMonth === 2 ? "cooperation and building relationships" : personalMonth === 3 ? "creative expression and communication" : personalMonth === 4 ? "practical work and organization" : personalMonth === 5 ? "freedom and positive changes" : personalMonth === 6 ? "family matters and responsibility" : personalMonth === 7 ? "spiritual growth and introspection" : personalMonth === 8 ? "material progress and achievement" : "completion and clearing out old patterns"}. Use this energy to align with your monthly theme.
            </p>
          </div>

          {/* Personal Day Guidance */}
          <div className="bg-white rounded-2xl p-6 border-2 border-pink-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold text-white shadow-lg"
                style={{ backgroundColor: getNumberColor(personalDay) }}
              >
                {personalDay}
              </div>
              <h4 className="text-lg font-bold text-pink-800">Personal Day {personalDay}</h4>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              Today's energy is perfect for {personalDay === 1 ? "starting new projects and taking leadership" : personalDay === 2 ? "collaborating and building relationships" : personalDay === 3 ? "creative activities and socializing" : personalDay === 4 ? "organizing and practical tasks" : personalDay === 5 ? "networking and trying new things" : personalDay === 6 ? "family time and helping others" : personalDay === 7 ? "meditation and spiritual practices" : personalDay === 8 ? "business matters and goal achievement" : "completing projects and releasing what's no longer needed"}. Plan your day accordingly.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Auspicious Footer */}
      <motion.div variants={itemVariants} className="bg-gradient-to-r from-saffron-100 to-orange-100 rounded-2xl p-6 border-2 border-saffron-300 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <span className="text-2xl">🙏</span>
          <p className="text-lg font-bold text-saffron-800">May these sacred numbers guide your path</p>
          <span className="text-2xl">🙏</span>
        </div>
        <p className="text-sm text-saffron-700">
          ॐ गं गणपतये नमः • Om Gam Ganapataye Namaha
        </p>
      </motion.div>
    </motion.div>
  );
}

// Mahadasha Timeline Component
function MahadashaTimeline({ mahadasha }) {
  const { t } = useTranslation();
  const allDashas = [mahadasha.current, ...mahadasha.upcoming];

  // Helper to translate planet names
  const translatePlanet = (planetName) => {
    return planetName;
  };

  // Get planet color
  const getPlanetColor = (planetName) => {
    const planetKey = planetName.toLowerCase();
    return PLANET_COLORS[planetKey] || '#666';
  };

  // Get planet symbol/icon
  const getPlanetSymbol = (planetName) => {
    const symbols = {
      'Sun': '☉',
      'Moon': '☽',
      'Mars': '♂',
      'Mercury': '☿',
      'Jupiter': '♃',
      'Venus': '♀',
      'Saturn': '♄',
      'Rahu': '☊',
      'Ketu': '☋'
    };
    return symbols[planetName] || '●';
  };

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-saffron-300 via-saffron-400 to-saffron-200 hidden md:block"></div>
      
      <div className="space-y-6">
        {allDashas.map((dasha, index) => {
          const isCurrent = index === 0;
          const planetColor = getPlanetColor(dasha.lord);
          const planetSymbol = getPlanetSymbol(dasha.lord);
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`relative flex items-start gap-6 ${
                isCurrent ? 'md:ml-0' : 'md:ml-0'
              }`}
            >
              {/* Timeline dot */}
              <div className="relative z-10 flex-shrink-0 hidden md:flex">
                <div 
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg border-4 ${
                    isCurrent 
                      ? 'bg-gradient-to-br from-saffron-400 to-saffron-600 border-white scale-110' 
                      : 'bg-white border-saffron-200'
                  }`}
                  style={{ 
                    color: isCurrent ? 'white' : planetColor,
                    boxShadow: isCurrent ? `0 4px 20px ${planetColor}40` : '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  {planetSymbol}
                </div>
              </div>

              {/* Content Card */}
              <div className={`flex-1 rounded-xl p-6 shadow-lg border-2 transition-all hover:shadow-xl ${
                isCurrent
                  ? 'bg-gradient-to-br from-saffron-50 via-saffron-100 to-cream-50 border-saffron-400'
                  : 'bg-white border-saffron-200 hover:border-saffron-300'
              }`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  {/* Left side - Planet info */}
                  <div className="flex items-center gap-4">
                    {/* Mobile planet icon */}
                    <div 
                      className={`md:hidden w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-md border-2 ${
                        isCurrent ? 'border-white' : 'border-saffron-200'
                      }`}
                      style={{ 
                        backgroundColor: planetColor,
                        color: 'white'
                      }}
                    >
                      {planetSymbol}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-bold text-saffron-800">
                          {translatePlanet(dasha.lord)} Mahadasha
                        </h3>
                        {isCurrent && (
                          <span className="px-2 py-1 text-xs font-semibold bg-saffron-500 text-white rounded-full animate-pulse">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-saffron-600">
                        <span className="font-semibold">{dasha.startYear}</span>
                        <span className="text-saffron-400">→</span>
                        <span className="font-semibold">{dasha.endYear}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Period and Strength */}
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-saffron-700">{dasha.period}</div>
                      <div className="text-xs text-saffron-500 uppercase tracking-wide">YEARS</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color: planetColor }}>
                        {dasha.strength}%
                      </div>
                      <div className="text-xs text-saffron-500 uppercase tracking-wide">STRENGTH</div>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="relative">
                  <div className="w-full bg-saffron-100 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${dasha.strength}%` }}
                      transition={{ duration: 1.5, delay: index * 0.1, ease: "easeOut" }}
                      className="h-full rounded-full relative overflow-hidden"
                      style={{ 
                        background: `linear-gradient(90deg, ${planetColor} 0%, ${planetColor}dd 100%)`,
                        boxShadow: `0 2px 8px ${planetColor}60`
                      }}
                    >
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        animate={{
                          x: ['-100%', '100%']
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                        style={{
                          width: '50%',
                          height: '100%'
                        }}
                      ></motion.div>
                    </motion.div>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-saffron-500">
                    <span>Strength: {dasha.strength}%</span>
                    <span>{dasha.endYear - dasha.startYear} Years</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Insight Card Component
function InsightCard({ insight, index, data }) {
  const { t } = useTranslation();
  
  // Translate insight title
  const getTranslatedTitle = (title) => {
    // Handle different title formats
    let titleKey = title.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '');
    
    // Map common variations
    if (titleKey.includes('ascendant') || titleKey.includes('lagna')) {
      titleKey = 'ascendantlagna';
    } else if (titleKey.includes('moonsign')) {
      titleKey = 'moonsign';
    } else if (titleKey.includes('strongplanetary') || titleKey.includes('planetaryinfluences')) {
      titleKey = 'strongplanetaryinfluences';
    } else if (titleKey.includes('birthnakshatra') || titleKey.includes('nakshatra')) {
      titleKey = 'birthnakshatra';
    }
    
    return title;
  };
  
  // Translate insight description based on type
  const getTranslatedDescription = (insight) => {
    if (insight.type === 'lagna') {
      // Try to get sign from data first, then from description
      let signKey = null;
      
      // Check if we have access to lagna sign from data
      if (data && data.lagna && data.lagna.sign) {
        signKey = data.lagna.sign.toLowerCase();
      } else {
        // Fallback: Extract sign from description
        const signMatch = insight.description.match(/\b(Aries|Taurus|Gemini|Cancer|Leo|Virgo|Libra|Scorpio|Sagittarius|Capricorn|Aquarius|Pisces)\b/i);
        if (signMatch) {
          signKey = signMatch[1].toLowerCase();
        }
      }
      
      if (signKey) {
        return insight.description;
      }
      return insight.description;
    } else if (insight.type === 'moon') {
      return insight.description;
    } else if (insight.type === 'nakshatra') {
      // Extract nakshatra and pada from description
      // Backend format: "Born under Hasta Nakshatra, Pada 2."
      let nakshatraMatch = insight.description.match(/Born under\s+(\w+(?:\s+\w+)?)\s+Nakshatra/i);
      if (!nakshatraMatch) {
        // Try alternative pattern: "under Hasta Nakshatra" or just "Hasta Nakshatra"
        nakshatraMatch = insight.description.match(/(?:under|Born under)\s+(\w+(?:\s+\w+)?)\s+(?:नक्षत्र|Nakshatra)/i);
      }
      if (!nakshatraMatch) {
        // Try pattern without "Born under": "Hasta Nakshatra"
        nakshatraMatch = insight.description.match(/(\w+(?:\s+\w+)?)\s+(?:नक्षत्र|Nakshatra)/i);
      }
      
      let padaMatch = insight.description.match(/Pada\s+(\d+)/i);
      if (!padaMatch) {
        // Try alternative: "पद 2" or "Pada 2"
        padaMatch = insight.description.match(/(?:पद|Pada)\s*(\d+)/i);
      }
      
      if (nakshatraMatch && padaMatch) {
        return insight.description;
      }
      return insight.description;
    } else if (insight.type === 'planets') {
      return insight.description;
    }
    return insight.description;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.05 }}
      className="bg-gradient-to-br from-saffron-50 to-cream-50 rounded-xl p-6 border border-saffron-200 shadow-lg"
    >
      <h3 className="text-xl font-bold text-saffron-700 mb-2">{getTranslatedTitle(insight.title)}</h3>
      <p className="text-saffron-600 leading-relaxed">{getTranslatedDescription(insight)}</p>
    </motion.div>
  );
}


export default Dashboard;

